/* Settings → Billing: the org's plan, credits, top-ups and statement, live from
   `/billing` (shared/api/billing.ts). Billing keeps no card: a purchase goes to
   the provider's page. The provider is picked for a first plan and for top-ups; a
   paid plan changes and renews with the provider it was bought through. */
import { useRef, useState } from "react";
import { CreditCard } from "lucide-react";
import { toast } from "sonner";
import {
  BILLING_OFFLINE, useBillingPlans, useBillingProviders, useBillingSummary, useCancelPlan, useCheckout,
  usePortal, useRevokeCancel, useRevokeChange, useSubscribe, useTopups,
} from "@/shared/api/billing";
import { useSettings } from "@/shared/api/settings";
import { Pane, Group, Row, RowList, GhostButton, PrimaryButton, StatusBadge } from "@/shared/components/settings-atoms";
import { PLANS } from "@/shared/lib/plans";
import ChangePlanDialog from "./ChangePlanDialog";
import CustomTopup from "./CustomTopup";
import { formatCredits, shortDate } from "./format";
import PlanCard from "./PlanCard";
import { openRazorpayCheckout } from "@/shared/lib/razorpayCheckout";
import StatementGroup from "./StatementGroup";
import UsageCard from "./UsageCard";

import type { TopupChoice } from "@/shared/api/billing";
import type { ApiError } from "@/shared/api/errors";
import type { BillingPlan, BillingSummary } from "@/shared/types/billing";

const PROVIDER_LABEL: Record<string, string> = { stripe: "Stripe", razorpay: "Razorpay" };
const OFFLINE = "Billing is unavailable right now";

/** One key per click: a retried request is the same purchase, a second click a new one. */
const freshKey = () => crypto.randomUUID();

const paneDesc = "Credits meter everything your agents do — chats, tasks and sandbox time.";

export default function BillingSection() {
  const summary = useBillingSummary();

  if (summary.isPending) {
    return (
      <Pane title="Billing" desc={paneDesc}>
        <div className="py-3 text-[12.5px] text-[var(--text-quaternary)] italic">Loading billing…</div>
      </Pane>
    );
  }
  if (summary.isError) {
    const err = summary.error as ApiError;
    return (
      <Pane title="Billing" desc={paneDesc}>
        <div role="alert" className="py-3 text-[13px] text-[var(--text-tertiary)]">
          {err.code === BILLING_OFFLINE ? OFFLINE : err.message}
          {err.request_id && <span className="ml-2 font-mono text-[11.5px] text-[var(--text-quaternary)]">ref {err.request_id}</span>}
        </div>
      </Pane>
    );
  }
  return <BillingAccount summary={summary.data} />;
}

function BillingAccount({ summary }: { summary: BillingSummary }) {
  const { data: settings } = useSettings();
  const canManage = settings?.org?.role === "owner" || settings?.org?.role === "admin";
  const { data: plans, error: plansError } = useBillingPlans();
  const { data: providers } = useBillingProviders();
  const { data: topups } = useTopups();
  const [picked, setPicked] = useState<string | null>(null);
  const [changing, setChanging] = useState<BillingPlan | null>(null);
  const subscribe = useSubscribe();
  const cancel = useCancelPlan();
  const revokeCancel = useRevokeCancel();
  const revokeChange = useRevokeChange();
  const checkout = useCheckout();
  const portal = usePortal();

  const { plan, subscription } = summary;
  // A top-up defaults to the provider the plan is paid through.
  const provider = picked ?? providers?.find((p) => p.name === subscription.provider)?.name ?? providers?.[0]?.name ?? null;
  const canTopUp = !!(topups?.packs.length || topups?.custom);
  const busy = subscribe.isPending || cancel.isPending || revokeCancel.isPending || revokeChange.isPending || checkout.isPending || portal.isPending;
  const copy = PLANS.find((p) => p.id === plan.name) ?? PLANS[0];
  const paid = !!plan.amount;
  const cancellation = subscription.cancellation;
  // Both can stay set on a Razorpay replacement billing can't revoke; the cancellation wins.
  const scheduled = cancellation ? null : subscription.scheduled_change;
  // Only monthly plans are listed (getBillingPlans), so a scheduled yearly SKU finds no match.
  const scheduledPlan = scheduled && plans?.find((p) => p.id === scheduled.plan_id);
  const scheduledName = scheduledPlan ? PLANS.find((p) => p.id === scheduledPlan.name)?.name : undefined;
  // A failed renewal leaves the scheduled change set, and past due is what needs fixing.
  const status = cancellation
    ? { label: `Cancels ${shortDate(cancellation.ends_at)}`, tone: "warn" as const }
    : subscription.status === "past_due"
      ? { label: "Past due", tone: "danger" as const }
      : scheduled
        ? { label: `Changes ${shortDate(scheduled.effective_at)}`, tone: "warn" as const }
        : { label: "Active", tone: "success" as const };

  const plansRef = useRef<HTMLDivElement>(null);
  const toPlans = () => plansRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" }); // jsdom has none
  const higher = PLANS.findIndex((p) => p.id === plan.name) < PLANS.length - 1;

  const choose = (target: BillingPlan) => {
    if (paid) return setChanging(target);
    if (!provider) return toast.error("No payment provider is available");
    subscribe.mutate(
      { plan_id: target.id, provider, idempotency_key: freshKey() },
      { onSuccess: (out) => !out.redirect_url && toast.success(`You're on ${PLANS.find((p) => p.id === target.name)?.name}`) },
    );
  };

  // A provider page leaves the app (useBillingMutation); a provider without one opens its checkout here.
  const manage = () =>
    portal.mutate(undefined, {
      onSuccess: async (out) => {
        if (!out.checkout) return;
        const changed = await openRazorpayCheckout(out.checkout).catch((e: Error) => {
          console.error(e); // the toast says what failed; the console keeps the cause
          toast.error("Could not open the payment page");
          return false;
        });
        if (changed) toast.success("Payment method updated");
      },
    });

  const buy = (choice: TopupChoice) => {
    if (!provider) return toast.error("No payment provider is available");
    checkout.mutate(
      { ...choice, provider, idempotency_key: freshKey() },
      { onSuccess: (out) => !out.redirect_url && toast.success("Credits added") },
    );
  };

  // A plan bought through one provider changes and renews there: only a first plan and top-ups pick one.
  const picker = (
    <div role="radiogroup" aria-label="Payment provider" className="divide-y divide-[var(--hairline)]">
      {(providers ?? []).map((pp) => {
        const active = provider === pp.name;
        return (
          <button
            key={pp.name}
            role="radio"
            aria-checked={active}
            onClick={() => setPicked(pp.name)}
            className="press w-full text-left py-3 flex items-center gap-3"
          >
            <span className={`w-4 h-4 rounded-full ring-1 flex items-center justify-center shrink-0 ${
              active ? "ring-[var(--text-primary)]" : "ring-[var(--hairline-focus)]"
            }`}>
              {active && <span className="w-2 h-2 rounded-full bg-[var(--text-primary)]" />}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[13.5px] font-medium text-[var(--text-primary)]">{PROVIDER_LABEL[pp.name] ?? pp.name}</span>
              <span className="block text-[12.5px] text-[var(--text-tertiary)]">Billed in {pp.currencies.join(", ")}</span>
            </span>
          </button>
        );
      })}
    </div>
  );

  // The two cards under the usage card: the plan and how it is paid.
  const card = "rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] shadow-lift p-5 min-w-0";
  return (
    <Pane title="Billing" desc={paneDesc}>
      <Group>
        <UsageCard summary={summary} onUpgrade={canManage && higher ? toPlans : undefined} />
      </Group>

      <Group>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div className={card}>
            <div className="flex items-center justify-between gap-3">
              <span className="text-[13px] text-[var(--text-tertiary)]">Current plan</span>
              <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
            </div>
            <div className="mt-2 text-[20px] font-semibold tracking-[-0.015em] text-[var(--text-primary)]">
              {copy.name} <span className="font-normal text-[var(--text-tertiary)]">· <span className="tabular-nums">${plan.amount ?? 0}</span>/mo</span>
            </div>
            <div className="mt-1 text-[12.5px] text-[var(--text-tertiary)]">
              {cancellation
                ? <>Ends on {shortDate(cancellation.ends_at)}, then back to Free</>
                : scheduled
                  ? <>Moves to {scheduledName ?? "a new plan"} on {shortDate(scheduled.effective_at)}</>
                  : paid && subscription.current_period_end
                  ? <>Renews on {shortDate(subscription.current_period_end)} · billed monthly</>
                  : "Free to start — upgrade any time"}
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-2">
              {canManage && higher && <PrimaryButton onClick={toPlans}>Change plan</PrimaryButton>}
              {/* Razorpay's link stays valid after payment (it doubles as its manage page), so only a plan not yet active asks for it */}
              {subscription.url && subscription.status !== "active" && (
                <GhostButton onClick={() => window.location.assign(subscription.url!)}>Complete payment</GhostButton>
              )}
              {paid && canManage && (cancellation
                ? <button disabled={busy} onClick={() => revokeCancel.mutate(undefined, { onSuccess: () => toast.success("Your plan carries on") })} className="press h-8 px-2 text-[13px] text-[var(--text-secondary)] hover:text-[var(--text-primary)] disabled:opacity-40">Keep plan</button>
                : scheduled
                  // Billing can't undo an authorised Razorpay replacement yet (definable.billing#46).
                  ? subscription.provider !== "razorpay" && <button disabled={busy} onClick={() => revokeChange.mutate(undefined, { onSuccess: () => toast.success(`You stay on ${copy.name}`) })} className="press h-8 px-2 text-[13px] text-[var(--text-secondary)] hover:text-[var(--text-primary)] disabled:opacity-40">Keep {copy.name}</button>
                  : <button disabled={busy} onClick={() => setChanging(plans?.find((p) => p.name === "free") ?? null)} className="press h-8 px-2 text-[13px] text-[var(--text-secondary)] hover:text-[var(--text-primary)] disabled:opacity-40">Cancel plan</button>)}
            </div>
          </div>

          <div className={`${card} py-2`}>
            {paid && subscription.provider ? (
              <div className="flex items-center gap-3 py-3 border-b hairline last:border-b-0">
                <span className="w-9 h-9 shrink-0 rounded-[10px] bg-[var(--surface-2)] flex items-center justify-center">
                  <CreditCard className="w-4 h-4 text-[var(--text-secondary)]" strokeWidth={1.75} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[13.5px] font-medium text-[var(--text-primary)]">
                    Paid through {PROVIDER_LABEL[subscription.provider] ?? subscription.provider}
                  </span>
                  <span className="block text-[12.5px] text-[var(--text-tertiary)]">Your card stays with them, not with us</span>
                </span>
                {canManage && <GhostButton disabled={busy} onClick={manage}>Manage billing</GhostButton>}
              </div>
            ) : (
              <div className="pt-3 pb-1">
                <div className="text-[13.5px] font-medium text-[var(--text-primary)]">Payment</div>
                <div className="text-[12.5px] text-[var(--text-tertiary)]">Purchases go to the provider's page. Your card stays with them, not with us.</div>
              </div>
            )}
            {!paid && picker}
          </div>
        </div>
      </Group>

      <Group title="Plans" desc={canManage ? "Switch any time — billing settles the difference." : "Only workspace owners and admins can change the plan."}>
        <div ref={plansRef} className="scroll-mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
          {PLANS.map((p) => {
            const price = plans?.find((bp) => bp.name === p.id);
            return (
              <PlanCard
                key={p.id}
                plan={p}
                price={price}
                currentId={plan.name}
                scheduledAt={price && price.id === scheduled?.plan_id ? scheduled.effective_at : undefined}
                disabled={!canManage || busy}
                onSelect={() => price && choose(price)}
              />
            );
          })}
        </div>
        {plansError && <div role="alert" className="pt-2 text-[12.5px] text-[var(--text-tertiary)]">{plansError.message}</div>}
      </Group>

      <Group title="Top-ups" desc="One-off credits on top of the plan's. They don't reset.">
        {/* A paid plan renews with its own provider, so the choice here is for top-ups only. */}
        {paid && canTopUp && <RowList header={<span>Pay top-ups with</span>} className="mb-3">{picker}</RowList>}
        {topups && canTopUp ? (
          <RowList>
            {topups.packs.map((pack) => (
              <Row key={pack.name} title={pack.display_name} desc={`${formatCredits(pack.credits)} credits`}>
                <GhostButton disabled={!canManage || busy} onClick={() => buy({ pack: pack.name })} className="disabled:opacity-40 disabled:pointer-events-none">
                  Buy · <span className="font-mono tabular-nums">${pack.amount}</span>
                </GhostButton>
              </Row>
            ))}
            {topups.custom && <CustomTopup bounds={topups.custom} disabled={!canManage || busy} onBuy={(amount) => buy({ amount })} />}
          </RowList>
        ) : (
          <RowList><div className="py-4 text-[12.5px] text-[var(--text-quaternary)] italic">No top-ups on offer.</div></RowList>
        )}
      </Group>

      <StatementGroup kind="grant" title="Payments" empty="No payments yet." />

      <ChangePlanDialog
        target={changing}
        currentName={copy.name}
        periodEnd={subscription.current_period_end}
        onClose={() => setChanging(null)}
      />
    </Pane>
  );
}
